import React, { FC, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { Avatar } from 'src/modules/messaging/components/Avatar';
import {
    BodyTextSmall,
    HeadingText10,
    HeadingText40,
    Modal,
    pxToRem,
} from '@modernhealth/vital-components';
import { ProviderType } from 'src/modules/user/models/user';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useProvider } from 'src/modules/providers/hooks/useProvider';
import { getMyProviders } from 'src/modules/providers/selectors/providersSelectors';
import specialties from 'src/modules/providers/assets/hand_with_heart.svg';
import location from 'src/modules/providers/assets/location_mark.svg';
import languages from 'src/modules/providers/assets/speak.svg';
import bio from 'src/modules/providers/assets/bio.svg';
import {
    formatProviderLocation,
    ProviderSpecialtyTypes,
} from 'src/modules/providers/components/ProviderCard';

interface ProviderDetailsModalProps {
    providerUuid: string;
    isVisible: boolean;
    onClose: VoidFunction;
}

export const ProviderDetailsModal: FC<ProviderDetailsModalProps> = ({
    providerUuid,
    isVisible,
    onClose,
}) => {
    const { t } = useTranslation();
    const { fetchMyProviders } = useProvider();
    const providers = useSelector(getMyProviders);
    const provider = useMemo(
        () => providers.find(p => p.uuid === providerUuid),
        [providerUuid, providers],
    );

    // If there is no provider found and the modal is visible,
    // fetch all providers to attempt to update provider data
    // and find a matching one.
    useEffect(() => {
        if (!provider && isVisible) {
            fetchMyProviders();
        }
    }, [isVisible, provider, fetchMyProviders]);

    if (!provider) {
        return null;
    }

    const getProviderDescription = () => {
        const providerTypeDescription: { [key: string]: string } = {
            [ProviderType.THERAPIST]: t('providers:provider_type.therapist'),
            [ProviderType.COACH]: t('providers:provider_type.coach'),
            [ProviderType.PSYCHIATRIST]: provider.psychiatristSubtype || '',
        };

        return providerTypeDescription[provider.providerType || ProviderType.COACH];
    };

    const providerSpecialties =
        provider.providerType === ProviderType.THERAPIST
            ? provider.providerSpecialties
            : provider.goalInterests;
    // Filter out 'Other' from specialties
    const filteredProviderSpecialties = (providerSpecialties || []).filter(
        specialty => specialty !== ProviderSpecialtyTypes.Other,
    );
    const sections = [
        {
            title: t('providers:provider_details.specialities'),
            description: filteredProviderSpecialties.join(', '),
            icon: specialties,
        },
        {
            title: t('providers:provider_details.languages'),
            description: provider?.languagePreferences?.join(', ') || '',
            icon: languages,
        },
        {
            title: t('providers:provider_details.location'),
            description:
                formatProviderLocation(
                    provider.country,
                    provider.city,
                    provider.state,
                    provider.zipCode,
                    provider.providerType,
                ) || '',
            icon: location,
        },
        {
            title: t('providers:provider_details.bio'),
            description: provider.introduction,
            icon: bio,
        },
    ];

    return (
        <Modal isVisible={isVisible} onCloseButtonClick={onClose}>
            <HeaderContainer>
                <Avatar diameter={60} user={provider} />
                <HeaderTextContainer>
                    <HeadingText40
                        marginBottom={4}
                    >{`${provider.firstName} ${provider.lastName}`}</HeadingText40>
                    <BodyTextSmall>{getProviderDescription()}</BodyTextSmall>
                </HeaderTextContainer>
            </HeaderContainer>
            <Content>
                {sections.map(
                    section =>
                        section.description && (
                            <Information key={section.title}>
                                <InformationHeader>
                                    <InformationIcon src={section.icon} />
                                    <HeadingText10 marginBottom={0}>{section.title}</HeadingText10>
                                </InformationHeader>
                                <BodyTextSmall>{section.description}</BodyTextSmall>
                            </Information>
                        ),
                )}
            </Content>
        </Modal>
    );
};

const HeaderContainer = styled.div({
    display: 'flex',
    marginBottom: pxToRem(5),
});

const HeaderTextContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginLeft: pxToRem(15),
});

const Content = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const Information = styled.div({
    marginTop: pxToRem(21),
});

const InformationHeader = styled.div({
    display: 'flex',
    alignItems: 'center',
    marginBottom: pxToRem(9),
});

const InformationIcon = styled.img({
    maxHeight: pxToRem(16),
    marginRight: pxToRem(9),
});
